// Le dossier d'une formation : toutes ses pièces, au même endroit.
//
// Une formatrice a signé les quatre lignes d'une feuille d'émargement le
// 21 octobre 2026, et rien n'en paraissait sur la formation : la convention
// vivait ici, la feuille sur l'écran Émargements, les attestations derrière un
// bouton de ce même écran, et le papier signé dans le dépôt. Un contrôle
// Qualiopi se prépare dossier par dossier, pas écran par écran.
//
// Ce bloc ne fabrique rien : il affiche ce que `/sessions/{id}/dossier`
// rassemble, et chaque ligne mène au document que sa source sait dessiner.
//
// **Ce qui manque est dit avant ce qui est là.** Un dossier qui tait ses trous
// laisse croire qu'il est complet, et c'est précisément ce qu'on corrige.
import React, { useCallback, useEffect, useState } from 'react';
import { FileText, ClipboardCheck, Award, Paperclip, Mail,
  Download, Loader2, AlertTriangle, CheckCircle2, Clock, MinusCircle,
  MessageSquare, Banknote } from 'lucide-react';
import api from '../api/client';
import { toast } from '../hooks/use-toast';
import { messageErreur } from '../lib/erreur';

const PICTO = {
  financement: Banknote,
  convention: FileText,
  emargement: ClipboardCheck,
  attestation: Award,
  piece: Paperclip,
  convocation: Mail,
};

// Quatre états, quatre couleurs — et une seule dit « c'est une preuve ».
const ETAT = {
  signe: { libelle: 'Signé', classe: 'bg-[#E6FBF7] text-[#0F766E]', Icone: CheckCircle2 },
  attente: { libelle: 'En attente', classe: 'bg-[#FEF3E7] text-[#B45309]', Icone: Clock },
  absent: { libelle: 'Absent', classe: 'bg-[#FEF6F6] text-[#B91C1C]', Icone: AlertTriangle },
  annule: { libelle: 'Annulée', classe: 'bg-[#F6F7FB] text-[#8E8FA5]', Icone: MinusCircle },
  disponible: { libelle: 'Disponible', classe: 'bg-[#F6F7FB] text-[#6B6C85]', Icone: Download },
  // Un refus n'est pas un manque : le dossier a ete instruit, et la reponse
  // est non. Le peindre en rouge d'absence ferait chercher ce qu'on n'a pas
  // fait.
  refuse: { libelle: 'Refusé', classe: 'bg-[#FEF6F6] text-[#B91C1C]', Icone: MinusCircle },
};

const jourFr = (iso) => {
  if (!iso) return '';
  try {
    return new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return iso; }
};

// Un téléchargement qui respecte le type reçu. `downloadPdf` force le PDF, ce
// qui est juste pour les documents que nous dessinons et faux pour une photo
// prise au téléphone : elle arriverait sous un nom en .pdf que rien n'ouvre.
async function telecharger(url, nomFichier) {
  const res = await api.get(url, { responseType: 'blob' });
  const type = res.headers?.['content-type'] || 'application/octet-stream';
  const href = URL.createObjectURL(new Blob([res.data], { type }));
  const a = document.createElement('a');
  a.href = href;
  a.setAttribute('download', nomFichier || 'document');
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(href);
}

export default function PanneauDossier({ sessionId }) {
  const [dossier, setDossier] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [occupe, setOccupe] = useState(null);

  const charger = useCallback(async () => {
    try {
      const { data } = await api.get(`/sessions/${sessionId}/dossier`);
      setDossier(data);
      setErreur(null);
    } catch (err) {
      setErreur(messageErreur(err));
    }
  }, [sessionId]);

  useEffect(() => { charger(); }, [charger]);

  const ouvrir = async (p) => {
    setOccupe(p.cle);
    try {
      await telecharger(p.url, p.nom_fichier);
    } catch (err) {
      toast({
        title: 'Document indisponible',
        description: messageErreur(err),
        variant: 'destructive',
      });
    } finally { setOccupe(null); }
  };

  if (erreur) {
    return (
      <div className="rounded-xl border border-[#EFF0F6] bg-[#FAFAFE] p-3.5 my-3"
           data-testid="dossier-erreur">
        <div className="text-[12.5px] text-[#B91C1C]">{erreur}</div>
      </div>
    );
  }
  if (!dossier) {
    return (
      <div className="rounded-xl border border-[#EFF0F6] bg-[#FAFAFE] p-3.5 my-3
                      text-[12px] text-[#8E8FA5]">
        Chargement des documents…
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-[#EFF0F6] bg-[#FAFAFE] p-3.5 my-3"
         data-testid={`dossier-${sessionId}`}>
      <div className="flex items-center justify-between mb-2.5">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-[#6B6C85]">
          Les documents de la formation
        </div>
        <span className="flex items-center gap-2">
          {dossier.alertes_ouvertes > 0 && (
            <span className="text-[11px] font-semibold text-[#B45309]"
                  title="Une alerte close sans dire ce qui a été fait ne prouve rien">
              {dossier.alertes_ouvertes} alerte{dossier.alertes_ouvertes > 1 ? 's' : ''}
            </span>
          )}
          <span className="text-[11px] font-semibold text-[#0F766E]"
                data-testid="dossier-preuves">
            {dossier.preuves} preuve{dossier.preuves > 1 ? 's' : ''}
          </span>
        </span>
      </div>

      {dossier.manque?.length > 0 && (
        <div className="rounded-lg border border-[#FCD9A6] bg-[#FEF3E7] p-2.5 mb-2.5"
             data-testid="dossier-manque">
          <div className="text-[11.5px] font-bold text-[#B45309] flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> Il manque encore
          </div>
          <ul className="mt-1 space-y-0.5">
            {dossier.manque.map((m) => (
              <li key={m} className="text-[11.5px] text-[#B45309]/90">• {m}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Les étapes du parcours. Ce ne sont pas des pièces : une étape est
          un geste qui a eu lieu ou non, et les mêler aux documents ferait
          chercher un PDF derrière « satisfaction : 2 réponses sur 3 ». */}
      {(dossier.etapes || []).length > 0 && (
        <div className="mb-3" data-testid="dossier-etapes">
          <div className="text-[10.5px] font-semibold uppercase tracking-wider text-[#8E8FA5] mb-1.5 flex items-center gap-1.5">
            <MessageSquare className="w-3 h-3" /> Le parcours
          </div>
          <ul className="space-y-1">
            {dossier.etapes.map((e) => {
              const etat = ETAT[e.etat] || ETAT.disponible;
              const EtatIcone = etat.Icone;
              return (
                <li key={e.code}
                    data-testid={`dossier-etape-${e.code}`}
                    className="flex items-center gap-2 rounded-lg bg-white border border-[#EFF0F6] px-2.5 py-1.5">
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-semibold text-[#1A1B2E] truncate">
                      {e.libelle}
                    </div>
                    <div className="text-[10.5px] text-[#8E8FA5] truncate">
                      {e.detail}
                    </div>
                  </div>
                  <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded ${etat.classe}`}>
                    <EtatIcone className="w-2.5 h-2.5" />
                    {e.etat === 'signe' ? 'Répondu'
                      : e.etat === 'attente' ? 'En attente' : 'Pas envoyé'}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <ul className="space-y-1.5">
        {dossier.pieces.map((p) => {
          const Icone = PICTO[p.genre] || FileText;
          const etat = ETAT[p.etat] || ETAT.disponible;
          const EtatIcone = etat.Icone;
          return (
            <li key={p.cle}
                data-testid={`dossier-piece-${p.cle}`}
                className="flex items-center gap-2.5 rounded-lg bg-white border border-[#EFF0F6] px-2.5 py-2">
              <Icone className="w-4 h-4 text-[#6B6C85] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[12.5px] font-semibold text-[#1A1B2E] truncate">
                  {p.titre}
                </div>
                <div className="text-[11px] text-[#8E8FA5] truncate">
                  {p.detail}{p.date ? ` · ${jourFr(p.date)}` : ''}
                  {p.origine === 'depose' ? ' · déposé à la main' : ''}
                </div>
              </div>
              <span className={`shrink-0 inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-0.5 rounded-md ${etat.classe}`}>
                <EtatIcone className="w-3 h-3" /> {etat.libelle}
              </span>
              {p.url ? (
                <button
                  onClick={() => ouvrir(p)}
                  disabled={!!occupe}
                  data-testid={`dossier-ouvrir-${p.cle}`}
                  title={`Ouvrir — ${p.titre}`}
                  aria-label={`Ouvrir ${p.titre}`}
                  className="shrink-0 w-8 h-8 rounded-lg border border-[#EFF0F6] text-[#4A4B63] flex items-center justify-center hover:border-[#C7D2FE] hover:text-[#4F46E5] disabled:opacity-50"
                >
                  {occupe === p.cle ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    : <Download className="w-3.5 h-3.5" />}
                </button>
              ) : (
                <span className="shrink-0 w-8 h-8" />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
